
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface ApiQuota {
  id: string;
  provider: string;
  quota_type: string;
  limit_value: number;
  current_usage: number;
  reset_at?: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

export const useApiQuotas = () => {
  const [quotas, setQuotas] = useState<ApiQuota[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchQuotas = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('api_quotas')
        .select('*')
        .order('provider', { ascending: true });

      if (error) throw error;
      setQuotas(data || []);
    } catch (err) {
      console.error('Error fetching API quotas:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch quotas');
    } finally {
      setLoading(false);
    }
  };

  const updateQuota = async (provider: string, quotaType: string, limitValue: number) => {
    try {
      const { error } = await supabase
        .from('api_quotas')
        .update({ 
          limit_value: limitValue,
          updated_at: new Date().toISOString()
        })
        .eq('provider', provider)
        .eq('quota_type', quotaType);

      if (error) throw error;
      
      // Update local state
      setQuotas(prev => 
        prev.map(quota => 
          quota.provider === provider && quota.quota_type === quotaType 
            ? { ...quota, limit_value: limitValue } 
            : quota
        )
      );
    } catch (err) {
      console.error('Error updating quota:', err);
      throw err;
    }
  };

  const resetQuotaUsage = async (provider: string, quotaType: string) => {
    try {
      const { error } = await supabase
        .from('api_quotas')
        .update({ 
          current_usage: 0,
          reset_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('provider', provider)
        .eq('quota_type', quotaType);

      if (error) throw error;
      
      // Update local state
      setQuotas(prev => 
        prev.map(quota => 
          quota.provider === provider && quota.quota_type === quotaType 
            ? { ...quota, current_usage: 0, reset_at: new Date().toISOString() } 
            : quota
        )
      );
    } catch (err) {
      console.error('Error resetting quota usage:', err);
      throw err;
    }
  };

  const getQuotaUsage = (provider: string, quotaType: string) => {
    const quota = quotas.find(q => q.provider === provider && q.quota_type === quotaType);
    return quota ? quota.current_usage / quota.limit_value : 0;
  };

  useEffect(() => {
    fetchQuotas();

    // Set up real-time subscription
    const channel = supabase
      .channel(`api-quotas-changes-${Math.random()}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'api_quotas'
      }, () => {
        fetchQuotas();
      })
      .subscribe();

    return () => {
      try {
        supabase.removeChannel(channel);
      } catch (error) {
        console.error('Error cleaning up API quotas subscription:', error);
      }
    };
  }, []);

  return {
    quotas,
    loading,
    error,
    updateQuota,
    resetQuotaUsage,
    getQuotaUsage,
    refetch: fetchQuotas
  };
};
